import * as React from "react";
import { LucideIcon } from "lucide-react";
import Image from "next/image";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";

type Quality = {
  id: number;
  title: string;
  description: string;
  icon: LucideIcon;
};

type QualityCardProps = {
  quality: Quality;
};

/**
 * Renders a quality card
 *
 * @param quality - The quality to render
 */
const QualityCard: React.FC<QualityCardProps> = ({ quality }) => {
  const containerRef = React.useRef<HTMLParagraphElement>(null);

  useGSAP(() => {
    gsap.fromTo(
      containerRef.current,
      { opacity: 0, y: 30 },
      {
        y: 0,
        opacity: 1,
        duration: 1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 90%",
        },
      }
    );
  }, []);

  const containerStyles = `
    border-primary-500/50 overflow xs:rounded-3xl xs:min-h-[400px] group group relative z-30 flex min-h-[350px] flex-col justify-between gap-y-5 overflow-hidden border-[0.2px] bg-[#13044dc9] p-8
  `;

  const containerBgStyles = `
    absolute inset-0 top-0 z-[-2] bg-[#13044dc9] bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,#3a0fc8bd,rgba(255,255,255,0))] opacity-50 transition-all duration-500 ease-in-out group-hover:opacity-75
  `;

  const titleStyles = `
    font-Acorn-Bold from-primary-800/5 via-primary-300 to-primary-200 vxs:text-4xl bg-gradient-to-t bg-clip-text text-3xl leading-[1.3] text-transparent mix-blend-luminosity
  `;

  return (
    <div ref={containerRef} className={containerStyles}>
      <div className={containerBgStyles} />
      <Image
        src="/images/boxes.svg"
        alt="boxes"
        fill
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover mix-blend-color-dodge brightness-110"
      />
      <div className="bg-primary-100 relative z-20 flex w-fit items-center justify-center rounded-full p-2.5">
        <quality.icon className="text-primary-900 xs:h-7 xs:w-7 h-6 w-6" />
      </div>
      <div className="flex flex-col gap-y-1">
        <h1 className={titleStyles}>{quality.title}</h1>
        <p className="text-primary-100 vxs:text-base relative text-sm !leading-[1.8]">
          {quality.description}
        </p>
      </div>
    </div>
  );
};

export default QualityCard;
